import React, { useState, useEffect, useCallback } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import http from "../Utils/axiosConfig";
import { apiMessage } from "../Utils/apiMessage";
import { downloadCsv } from "../Utils/csv";
import { onlyNumber, toISODate } from "../Utils/commonFuntion";
import ToastService from "../Utils/ToastService";
import "./DairyRegister.css";

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

const today = toISODate();
const oneMonthBack = new Date();
oneMonthBack.setMonth(oneMonthBack.getMonth() - 1);
const defaultFrom = toISODate(oneMonthBack);

// Same IST cutoff used in MemberRegister/Main/DashBoard.js: 1 AM-3 PM = morning session
const getDefaultSession = () => {
  const now = new Date();
  const istOffset = 5.5 * 60;
  const localOffset = now.getTimezoneOffset();
  const istDate = new Date(now.getTime() + (istOffset + localOffset) * 60000);
  const currentHour = istDate.getHours();
  return currentHour >= 1 && currentHour < 15 ? "morning" : "evening";
};

const fmt = (v) => (v != null ? Number(v).toFixed(2) : "0.00");
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      })
    : "-";

// संघ रवाना / घट/वाढ / नफा/तोटा are derived on read rather than read from the
// stored extra_milk/extra_rate columns: those were written as
// `entered − collected` without subtracting the local sale, so they're wrong
// for any row that had one. Deriving here means historical rows come out
// right with no backfill.
const deriveRow = (r) => {
  const dispatchMilk = Number(r.collected_milk || 0) - Number(r.sale_milk || 0);
  const dispatchRate = Number(r.collected_rate || 0) - Number(r.sale_rate || 0);
  return {
    dispatchMilk,
    dispatchRate,
    ghatVadh: Number(r.milk || 0) - dispatchMilk,
    nafaTota: Number(r.total_rate || 0) - dispatchRate,
  };
};

const buildInitForm = () => ({
  date: today,
  session: getDefaultSession(),
  type: "buffalo",
  milk: "",
  fat: "",
  snf: "",
  grade: "G",
  rate_per_ltr: "",
  total_rate: "",
});

const DairyRegister = () => {
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(buildInitForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  // Summary fetched from API
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Detail tab
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(today);
  const [rows, setRows] = useState([]);
  const [fetching, setFetching] = useState(false);

  // Period (10-day) summary + drill-down
  const [periodRows, setPeriodRows] = useState([]);
  const [periodFetching, setPeriodFetching] = useState(false);
  const [viewPeriod, setViewPeriod] = useState(null); // {from_date, to_date} or null

  // Auto-calc total rate
  useEffect(() => {
    const m = parseFloat(form.milk);
    const r = parseFloat(form.rate_per_ltr);
    if (m > 0 && r > 0) {
      setForm((prev) => ({ ...prev, total_rate: (m * r).toFixed(2) }));
    }
  }, [form.milk, form.rate_per_ltr]);

  // Fetch summary whenever date, session or type changes
  const fetchSummary = useCallback(async (date, session, type) => {
    if (!date || !session || !type) return;
    setSummaryLoading(true);
    setSummary(null);
    try {
      const res = await http.post("dairy-register/summary", { date, session, type });
      if (res?.code === "DATA_FOUND") setSummary(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary(form.date, form.session, form.type);
  }, [form.date, form.session, form.type]);

  // Derived computed values.
  // "entered" here is the संघ पावती — the receipt back from the collection
  // centre — not a total. What was actually dispatched is संघ रवाना:
  // collection minus whatever was sold locally before dispatch. घट/वाढ and
  // नफा/तोटा measure the receipt against that, so the local sale has to come
  // out first (it previously didn't, overstating the gap on any day with a sale).
  const enteredMilk = form.milk !== "" ? parseFloat(form.milk) : null;
  const enteredRate = form.total_rate !== "" ? parseFloat(form.total_rate) : null;
  const collMilk = summary ? Number(summary.collected_milk) : 0;
  const collRate = summary ? Number(summary.collected_rate) : 0;
  const saleMilk = summary ? Number(summary.sale_milk) : 0;
  const saleRate = summary ? Number(summary.sale_rate) : 0;
  const dispatchMilk = collMilk - saleMilk;
  const dispatchRate = collRate - saleRate;
  const extraMilk = enteredMilk !== null ? enteredMilk - dispatchMilk : null;
  const extraRate = enteredRate !== null ? enteredRate - dispatchRate : null;
  const noCollection = !summaryLoading && summary !== null && collMilk === 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setSuccess(false);
  };

  const validate = () => {
    const e = {};
    if (!form.date) e.date = "तारीख आवश्यक आहे";
    if (!form.milk) e.milk = "दूध आवश्यक आहे";
    else if (parseFloat(form.milk) < 0) e.milk = "दूध ऋण असू शकत नाही";
    if (!form.fat) e.fat = "फॅट आवश्यक आहे";
    else if (parseFloat(form.fat) < 0) e.fat = "फॅट ऋण असू शकत नाही";
    if (!form.snf) e.snf = "SNF आवश्यक आहे";
    else if (parseFloat(form.snf) < 0) e.snf = "SNF ऋण असू शकत नाही";
    if (!form.rate_per_ltr) e.rate_per_ltr = "दर आवश्यक आहे";
    else if (parseFloat(form.rate_per_ltr) < 0) e.rate_per_ltr = "दर ऋण असू शकत नाही";
    if (form.total_rate && parseFloat(form.total_rate) < 0)
      e.total_rate = "एकूण दर ऋण असू शकत नाही";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        ...form,
        collected_milk: collMilk,
        collected_rate: collRate,
        extra_milk: extraMilk,
        extra_rate: extraRate,
        sale_milk: saleMilk,
        sale_rate: saleRate,
      };
      const res = await http.post("dairy-register/add", payload);
      if (res?.code === "ENTRY_ADDED") {
        setSuccess(true);
        setForm(buildInitForm());
        setSummary(null);
        ToastService.success("नोंद जतन झाली! ✅");
      } else if (res?.code === "ENTRY_ALREADY_EXISTS") {
        ToastService.warning("या तारखेसाठी, वेळेसाठी व प्रकारासाठी नोंद आधीच झाली आहे!");
      } else {
        ToastService.error("नोंद जतन अयशस्वी ❌");
      }
    } catch (err) {
      console.error(err);
      ToastService.error(apiMessage());
    } finally {
      setLoading(false);
    }
  };

  const fetchEntries = async (f = from, t = to) => {
    setFetching(true);
    try {
      const res = await http.post("dairy-register/entries", {
        from_date: f,
        to_date: t,
      });
      setRows(res?.code === "DATA_FOUND" ? res.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  const fetchPeriodSummary = async (f = from, t = to) => {
    setPeriodFetching(true);
    try {
      const res = await http.post("dairy-register/period-summary", {
        from_date: f,
        to_date: t,
      });
      setPeriodRows(res?.code === "DATA_FOUND" ? res.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setPeriodFetching(false);
    }
  };

  useEffect(() => {
    if (tab === 1) fetchPeriodSummary();
  }, [tab]);

  const openPeriodDetail = (r) => {
    setViewPeriod({ from_date: r.from_date, to_date: r.to_date });
    fetchEntries(r.from_date, r.to_date);
  };

  const handleExportEntries = () => {
    if (!viewPeriod) return;
    downloadCsv(
      `dairy-register-${viewPeriod.from_date}-to-${viewPeriod.to_date}.csv`,
      [
        ["तारीख", (r) => fmtDate(r.date)],
        ["वेळ", (r) => (r.session === "morning" ? "सकाळ" : "संध्याकाळ")],
        ["प्रकार", (r) => (r.type === "buffalo" ? "म्हैस" : r.type === "cow" ? "गाय" : "एकत्र")],
        ["स्वसंस्थेचे संकलन", (r) => fmt(r.collected_milk)],
        ["संकलन किंमत", (r) => fmt(r.collected_rate)],
        ["स्थानिक विक्री", (r) => fmt(r.sale_milk)],
        ["विक्री किंमत", (r) => fmt(r.sale_rate)],
        ["संघ रवाना", (r) => fmt(deriveRow(r).dispatchMilk)],
        ["संघ पावती दूध", (r) => fmt(r.milk)],
        ["संघ पावती फॅट", (r) => fmt(r.fat)],
        ["संघ पावती SNF", (r) => fmt(r.snf)],
        ["संघ पावती दर्जा", (r) => r.grade],
        ["संघ पावती दर/लि.", (r) => fmt(r.rate_per_ltr)],
        ["संघ पावती किंमत", (r) => fmt(r.total_rate)],
        ["घट/वाढ", (r) => fmt(deriveRow(r).ghatVadh)],
        ["नफा/तोटा", (r) => fmt(deriveRow(r).nafaTota)],
      ],
      rows,
    );
  };

  return (
    <div className="dre-container">
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="fullWidth"
        centered
        textColor="inherit"
        TabIndicatorProps={{ style: { backgroundColor: "#ff9800" } }}
        sx={{
          "& .MuiTab-root": {
            color: "#bbb",
            fontWeight: "bold",
            fontSize: "16px",
          },
          "& .Mui-selected": { color: "#ff9800" },
        }}
      >
        <Tab label="नोंद" />
        <Tab label="माहिती" />
      </Tabs>

      {/* ── ENTRY TAB ── */}
      <TabPanel value={tab} index={0}>
        {noCollection && (
          <div className="dre-no-collection-banner">
            या दिवशी व वेळेसाठी दूध संकलन झालेले नाही.
          </div>
        )}
        <form className="dre-form" onSubmit={handleSubmit}>
          {/* Date */}
          <div className="dre-field">
            <input
              type="date"
              name="date"
              value={form.date}
              onChange={handleChange}
              className="dre-date-input"
            />
            {errors.date && <p className="dre-error">{errors.date}</p>}
          </div>

          {/* Session */}
          <div className="dre-field dre-radio-row">
            <label className="dre-field-label">वेळ</label>
            <div className="dre-radios">
              {[
                ["morning", "सकाळ"],
                ["evening", "संध्याकाळ"],
              ].map(([v, l]) => (
                <label key={v} className="dre-radio-label">
                  <input
                    type="radio"
                    name="session"
                    value={v}
                    checked={form.session === v}
                    onChange={handleChange}
                  />
                  {l}
                </label>
              ))}
            </div>
          </div>

          {/* Type */}
          <div className="dre-field dre-radio-row">
            <label className="dre-field-label">प्रकार</label>
            <div className="dre-radios">
              {[
                ["buffalo", "म्हैस"],
                ["cow", "गाय"],
                ["mixed", "एकत्र"],
              ].map(([v, l]) => (
                <label key={v} className="dre-radio-label">
                  <input
                    type="radio"
                    name="type"
                    value={v}
                    checked={form.type === v}
                    onChange={handleChange}
                  />
                  {l}
                </label>
              ))}
            </div>
          </div>

          {/* संघ पावती — everything below is what came back from the
              collection centre, not the dairy's own collection figures. */}
          <div className="dre-section-label">संघ पावती</div>

          {/* Milk */}
          <div className="dre-field dre-inline">
            <label className="dre-field-label"><span className="dre-var">A</span>दूध:</label>
            <input
              type="number"
              name="milk"
              value={form.milk}
              onChange={handleChange}
              onKeyDown={onlyNumber}
              min="0"
              step="any"
              placeholder="लिटर"
              className="dre-input"
            />
            {errors.milk && <p className="dre-error">{errors.milk}</p>}
          </div>

          {/* Fat + SNF */}
          <div className="dre-field dre-two-col">
            <div className="dre-inline">
              <label className="dre-field-label">फॅट:</label>
              <input
                type="number"
                name="fat"
                value={form.fat}
                onChange={handleChange}
                onKeyDown={onlyNumber}
                min="0"
                step="any"
                placeholder="%"
                className="dre-input"
              />
              {errors.fat && <p className="dre-error">{errors.fat}</p>}
            </div>
            <div className="dre-inline">
              <label className="dre-field-label">एस.एन.एफ:</label>
              <input
                type="number"
                name="snf"
                value={form.snf}
                onChange={handleChange}
                onKeyDown={onlyNumber}
                min="0"
                step="any"
                placeholder="%"
                className="dre-input"
              />
              {errors.snf && <p className="dre-error">{errors.snf}</p>}
            </div>
          </div>

          {/* Grade */}
          <div className="dre-field dre-radio-row">
            <label className="dre-field-label">दर्जा</label>
            <div className="dre-radios">
              {["G", "S", "U", "R"].map((g) => (
                <label key={g} className="dre-radio-label">
                  <input
                    type="radio"
                    name="grade"
                    value={g}
                    checked={form.grade === g}
                    onChange={handleChange}
                  />
                  {g}
                </label>
              ))}
            </div>
          </div>

          {/* Rate */}
          <div className="dre-field dre-inline">
            <label className="dre-field-label"><span className="dre-var">B</span>दर/लिटर:</label>
            <input
              type="number"
              name="rate_per_ltr"
              value={form.rate_per_ltr}
              onChange={handleChange}
              onKeyDown={onlyNumber}
              min="0"
              step="any"
              className="dre-input"
            />
            {errors.rate_per_ltr && (
              <p className="dre-error">{errors.rate_per_ltr}</p>
            )}
          </div>

          {/* Total rate */}
          <div className="dre-field dre-inline">
            <label className="dre-field-label">
              <span className="dre-var">C</span>एकूण दर:
              <span className="dre-field-formula">C = A × B</span>
            </label>
            <input
              type="number"
              name="total_rate"
              value={form.total_rate}
              onChange={handleChange}
              onKeyDown={onlyNumber}
              min="0"
              step="any"
              className="dre-input"
            />
            {errors.total_rate && (
              <p className="dre-error">{errors.total_rate}</p>
            )}
          </div>

          {/* ── Summary Panel ── */}
          <div className="dre-summary-panel">
            <div className="dre-summary-title">
              {summaryLoading ? "माहिती लोड होत आहे..." : "सत्र सारांश"}
            </div>
            {!summaryLoading && (
              <div className="dre-summary-grid">
                <div className="dre-summary-item">
                  <span className="dre-summary-label"><span className="dre-var">D</span>स्वसंस्थेचे संकलन</span>
                  <span className="dre-summary-value">{fmt(collMilk)} लि.</span>
                  <span className="dre-summary-formula">सभासद नोंदींची बेरीज</span>
                </div>
                <div className="dre-summary-item">
                  <span className="dre-summary-label"><span className="dre-var">E</span>संकलन किंमत</span>
                  <span className="dre-summary-value">₹{fmt(collRate)}</span>
                  <span className="dre-summary-formula">सभासद नोंदींची बेरीज</span>
                </div>
                <div className="dre-summary-item">
                  <span className="dre-summary-label"><span className="dre-var">F</span>स्थानिक विक्री</span>
                  <span className="dre-summary-value">{fmt(saleMilk)} लि.</span>
                  <span className="dre-summary-formula">डेअरी विक्री नोंदी</span>
                </div>
                <div className="dre-summary-item">
                  <span className="dre-summary-label"><span className="dre-var">G</span>विक्री किंमत</span>
                  <span className="dre-summary-value">₹{fmt(saleRate)}</span>
                  <span className="dre-summary-formula">डेअरी विक्री नोंदी</span>
                </div>
                {/* What actually went to the collection centre — the figure the
                    संघ पावती being typed in above should be compared against.
                    Litres only: the money equivalent (dispatchRate) mixes a cost
                    with sale revenue, so it isn't a price anyone paid and would
                    mislead if shown. It's still used to derive नफा/तोटा below. */}
                <div className="dre-summary-item dre-summary-item--key">
                  <span className="dre-summary-label"><span className="dre-var">H</span>संघ रवाना</span>
                  <span className="dre-summary-value">{fmt(dispatchMilk)} लि.</span>
                  <span className="dre-summary-formula">H = D − F</span>
                </div>
                {/* Blank slot so the 2-column grid keeps its litres/किंमत
                    rhythm and घट/वाढ + नफा/तोटा stay paired on their own row. */}
                <div className="dre-summary-item--blank" aria-hidden="true"></div>
                <div
                  className={`dre-summary-item ${extraMilk !== null && extraMilk < 0 ? "dre-summary-item--neg" : ""}`}
                >
                  <span className="dre-summary-label"><span className="dre-var">I</span>घट/वाढ</span>
                  <span className="dre-summary-value">
                    {extraMilk !== null ? `${fmt(extraMilk)} लि.` : "-"}
                  </span>
                  <span className="dre-summary-formula">I = A − H</span>
                </div>
                <div
                  className={`dre-summary-item ${extraRate !== null && extraRate < 0 ? "dre-summary-item--neg" : ""}`}
                >
                  <span className="dre-summary-label"><span className="dre-var">J</span>नफा/तोटा</span>
                  <span className="dre-summary-value">
                    {extraRate !== null ? `₹${fmt(extraRate)}` : "-"}
                  </span>
                  <span className="dre-summary-formula">J = C − (E − G)</span>
                </div>
              </div>
            )}
          </div>

          {success && <p className="dre-success">✅ नोंद जतन झाली!</p>}

          <div className="dre-submit-row">
            <button
              type="submit"
              className="dre-submit-btn"
              disabled={loading || summaryLoading || noCollection}
            >
              {loading ? "जतन होत आहे..." : "OKAY"}
            </button>
          </div>
        </form>
      </TabPanel>

      {/* ── DETAIL TAB ── */}
      <TabPanel value={tab} index={1}>
        <div className="dre-detail">
          {viewPeriod === null ? (
            <>
              <div className="dre-filter-bar">
                <div className="dre-filter-group">
                  <label>पासून</label>
                  <input
                    type="date"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                  />
                </div>
                <div className="dre-filter-group">
                  <label>पर्यंत</label>
                  <input
                    type="date"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                  />
                </div>
                <button
                  className="dre-search-btn"
                  onClick={() => fetchPeriodSummary()}
                  disabled={periodFetching}
                >
                  {periodFetching ? "..." : "शोधा"}
                </button>
              </div>

              <div className="dre-table-wrap">
                {periodRows.length === 0 ? (
                  <div className="dre-no-data">
                    <p>या कालावधीसाठी नोंदी उपलब्ध नाहीत.</p>
                  </div>
                ) : (
                  <table className="dre-table">
                    <thead>
                      <tr>
                        <th rowSpan={2}>पासून</th>
                        <th rowSpan={2}>पर्यंत</th>
                        <th rowSpan={2}>स्वसंस्थेचे संकलन</th>
                        <th rowSpan={2}>किंमत</th>
                        <th rowSpan={2}>स्थानिक विक्री</th>
                        <th rowSpan={2}>किंमत</th>
                        <th rowSpan={2}>संघ रवाना</th>
                        <th colSpan={2} className="dre-group-head">संघ पावती</th>
                        <th rowSpan={2}>घट/वाढ(लि)</th>
                        <th rowSpan={2}>नफा/तोटा(₹)</th>
                        <th rowSpan={2}></th>
                      </tr>
                      <tr>
                        <th>दूध</th>
                        <th>किंमत</th>
                      </tr>
                    </thead>
                    <tbody>
                      {periodRows.map((r, i) => (
                        <tr key={i}>
                          <td>{fmtDate(r.from_date)}</td>
                          <td>{fmtDate(r.to_date)}</td>
                          <td>{fmt(r.collected_milk)}</td>
                          <td>{fmt(r.collected_rate)}</td>
                          <td>{fmt(r.sale_milk)}</td>
                          <td>{fmt(r.sale_rate)}</td>
                          <td>{fmt(r.dispatch_milk)}</td>
                          <td>{fmt(r.total_milk)}</td>
                          <td className="dre-amount">{fmt(r.total_rate)}</td>
                          <td
                            className={
                              Number(r.extra_milk) < 0 ? "dre-neg" : "dre-pos"
                            }
                          >
                            {fmt(r.extra_milk)}
                          </td>
                          <td
                            className={
                              Number(r.extra_rate) < 0 ? "dre-neg" : "dre-pos"
                            }
                          >
                            {fmt(r.extra_rate)}
                          </td>
                          <td>
                            <button
                              className="dre-search-btn"
                              onClick={() => openPeriodDetail(r)}
                            >
                              तपशील
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="dre-filter-bar">
                <button
                  className="dre-search-btn"
                  onClick={() => setViewPeriod(null)}
                >
                  ← मागे
                </button>
                <span>
                  {fmtDate(viewPeriod.from_date)} ते {fmtDate(viewPeriod.to_date)}
                </span>
                <button className="dre-search-btn" onClick={handleExportEntries}>
                  ⬇ Download
                </button>
              </div>

              <div className="dre-table-wrap">
                {fetching ? (
                  <div className="dre-no-data">
                    <p>लोड होत आहे...</p>
                  </div>
                ) : rows.length === 0 ? (
                  <div className="dre-no-data">
                    <p>या कालावधीसाठी नोंदी उपलब्ध नाहीत.</p>
                  </div>
                ) : (
                  <table className="dre-table">
                    <thead>
                      <tr>
                        <th rowSpan={2}>तारीख</th>
                        <th rowSpan={2}>वेळ</th>
                        <th rowSpan={2}>प्रकार</th>
                        <th rowSpan={2}>स्वसंस्थेचे संकलन</th>
                        <th rowSpan={2}>किंमत</th>
                        <th rowSpan={2}>स्थानिक विक्री</th>
                        <th rowSpan={2}>किंमत</th>
                        <th rowSpan={2}>संघ रवाना</th>
                        <th colSpan={6} className="dre-group-head">संघ पावती</th>
                        <th rowSpan={2}>घट/वाढ(लि)</th>
                        <th rowSpan={2}>नफा/तोटा(₹)</th>
                      </tr>
                      <tr>
                        <th>दूध</th>
                        <th>फॅट</th>
                        <th>SNF</th>
                        <th>दर्जा</th>
                        <th>दर/लि.</th>
                        <th>किंमत</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => {
                        const d = deriveRow(r);
                        return (
                          <tr key={i}>
                            <td>{fmtDate(r.date)}</td>
                            <td>{r.session === "morning" ? "सकाळ" : "संध्याकाळ"}</td>
                            <td>
                              {r.type === "buffalo"
                                ? "म्हैस"
                                : r.type === "cow"
                                  ? "गाय"
                                  : "एकत्र"}
                            </td>
                            <td>{fmt(r.collected_milk)}</td>
                            <td>{fmt(r.collected_rate)}</td>
                            <td>{fmt(r.sale_milk)}</td>
                            <td>{fmt(r.sale_rate)}</td>
                            <td>{fmt(d.dispatchMilk)}</td>
                            <td>{fmt(r.milk)}</td>
                            <td>{fmt(r.fat)}</td>
                            <td>{fmt(r.snf)}</td>
                            <td>{r.grade}</td>
                            <td>{fmt(r.rate_per_ltr)}</td>
                            <td className="dre-amount">{fmt(r.total_rate)}</td>
                            <td className={d.ghatVadh < 0 ? "dre-neg" : "dre-pos"}>
                              {fmt(d.ghatVadh)}
                            </td>
                            <td className={d.nafaTota < 0 ? "dre-neg" : "dre-pos"}>
                              {fmt(d.nafaTota)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </TabPanel>
    </div>
  );
};

export default DairyRegister;
